"use client";

import { useState } from "react";
import type { CSSProperties } from "react";
import { Confirm } from "@/components/shared/Confirm";
import { Button } from "@/components/ui/button";
import { InfoCard } from "@/components/ui/info-card";
import { useDevice } from "@/hooks";
import { useSolanaWallet } from "@/hooks/solana";
import { cn } from "@/lib/utils";
import { truncateAddress } from "@/utils/address";
import { LogOut, Wallet, ExternalLink } from "lucide-react";

interface ProfileSolanaWalletProps {
  className?: string;
  style?: CSSProperties;
}

/**
 * Profile component for managing Solana wallet linking.
 *
 * This allows users to:
 * - See their linked Solana wallet address
 * - Unlink their Solana wallet (to link it to a different EVM account)
 * - Link a new Solana wallet if none is linked
 *
 * Note: Solana wallet is used ONLY for transaction signing on Solana DEXs,
 * NOT for authentication (auth is always via EVM wallet).
 */
export function ProfileSolanaWallet({ className, style }: ProfileSolanaWalletProps) {
  const [isUnlinkConfirmOpen, setIsUnlinkConfirmOpen] = useState(false);
  const { isMobile } = useDevice();

  const {
    linkedSolanaAddress,
    hasLinkedSolanaWallet,
    unlinkSolanaWallet,
    isUnlinking,
    connectAndLinkSolanaWallet,
    linkError,
    clearLinkError,
    isAlreadyLinkedError,
  } = useSolanaWallet();

  const handleUnlink = async () => {
    await unlinkSolanaWallet();
    setIsUnlinkConfirmOpen(false);
  };

  const handleLink = async () => {
    clearLinkError();
    await connectAndLinkSolanaWallet();
  };

  const showBackground = !isMobile;
  const padding = isMobile ? "none" : "md";

  return (
    <>
      {/* Unlink confirmation dialog */}
      <Confirm
        open={isUnlinkConfirmOpen}
        title="Unlink Solana Wallet?"
        description={
          "This will unlink your Solana wallet from this account. " +
          "You can then link it to a different account, or re-link it here later."
        }
        confirmText="Yes, unlink"
        onConfirm={handleUnlink}
        onClose={() => setIsUnlinkConfirmOpen(false)}
      />

      {/* Error dialog for "already linked" error */}
      <Confirm
        open={isAlreadyLinkedError}
        title="Wallet Already Linked"
        description={linkError?.message || "This wallet is already linked to another account."}
        confirmText="I understand"
        onConfirm={clearLinkError}
        onClose={clearLinkError}
      />

      <InfoCard
        title="Solana Wallet"
        padding={padding}
        withBackground={showBackground}
        withBorder={showBackground}
        gap="md"
        className={cn("flex w-full flex-col overflow-hidden", isMobile && "px-0 py-4", className)}
        contentClassName="flex min-h-0 flex-1 flex-col gap-0 overflow-hidden"
        style={style}
      >
        <div className="flex items-center justify-between py-3 md:py-4">
          {/* Wallet Info */}
          <div className="flex min-w-0 items-center gap-3">
            <span className="flex size-10 items-center justify-center rounded-full bg-cede-primary/10 text-cede-primary">
              <Wallet className="h-5 w-5" />
            </span>
            <div className="flex min-w-0 flex-col">
              <span className="truncate text-base font-medium text-cede-text-primary">
                {hasLinkedSolanaWallet ? "Linked Wallet" : "No wallet linked"}
              </span>
              {linkedSolanaAddress && (
                <span className="truncate text-sm text-cede-text-secondary">
                  {truncateAddress(linkedSolanaAddress)}
                </span>
              )}
              {!hasLinkedSolanaWallet && (
                <span className="truncate text-sm text-cede-text-secondary">
                  Link a Solana wallet to trade on Solana DEXs
                </span>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2">
            {hasLinkedSolanaWallet ? (
              <Button
                variant="unlink"
                size="small"
                onClick={() => setIsUnlinkConfirmOpen(true)}
                disabled={isUnlinking}
                loading={isUnlinking}
                leftIcon={<LogOut className="h-4 w-4" />}
              >
                {isUnlinking ? "Unlinking..." : "Unlink"}
              </Button>
            ) : (
              <Button
                variant="secondary"
                size="small"
                onClick={handleLink}
                rightIcon={<ExternalLink className="h-4 w-4" />}
              >
                Link
              </Button>
            )}
          </div>
        </div>

        {/* Helper text */}
        <p className="text-xs text-cede-text-disabled">
          Your Solana wallet is used for signing transactions on Solana DEXs like Jupiter.
          {hasLinkedSolanaWallet && " Unlinking allows you to link this wallet to a different account."}
        </p>
      </InfoCard>
    </>
  );
}
